/**
 * Pinia persistence for oauth2Store: localStorage when "Remember me", otherwise sessionStorage.
 * Marker `${key}:remember` in localStorage records which backing store holds the payload.
 */

const markerSuffix = ':remember'

export function getOauth2PersistStorageKey(): string {
  return (import.meta.env.VITE_LOCAL_STORAGE_PREFIX || '') + 'oauth2Store'
}

function rememberMarkerKey(storageKey: string): string {
  return `${storageKey}${markerSuffix}`
}

export function getOauth2RememberMarkerKey(): string {
  return rememberMarkerKey(getOauth2PersistStorageKey())
}

/** Remove persisted oauth2 snapshot and mode marker (both storages). */
export function clearOauth2Persistence(storageKey: string): void {
  try {
    localStorage.removeItem(rememberMarkerKey(storageKey))
    localStorage.removeItem(storageKey)
    sessionStorage.removeItem(storageKey)
  } catch {
    /* ignore */
  }
}

type PersistedOauth2Shape = {
  rememberMe?: boolean
  tokenInfo?: { accessToken?: string } | null
}

export const oauth2PersistStorage: Pick<Storage, 'getItem' | 'setItem'> = {
  getItem(key: string): string | null {
    try {
      const marker = localStorage.getItem(rememberMarkerKey(key))
      if (marker === 'persistent') {
        return localStorage.getItem(key)
      }
      if (marker === 'session') {
        return sessionStorage.getItem(key)
      }
      const legacyLs = localStorage.getItem(key)
      if (legacyLs) {
        try {
          const parsed = JSON.parse(legacyLs) as PersistedOauth2Shape
          if (parsed?.tokenInfo?.accessToken) {
            return legacyLs
          }
        } catch {
          /* ignore */
        }
      }
      return sessionStorage.getItem(key)
    } catch {
      return null
    }
  },

  setItem(key: string, value: string): void {
    try {
      let remember = false
      try {
        const data = JSON.parse(value) as PersistedOauth2Shape
        remember = data.rememberMe === true
      } catch {
        remember = false
      }
      if (remember) {
        localStorage.setItem(rememberMarkerKey(key), 'persistent')
        localStorage.setItem(key, value)
        sessionStorage.removeItem(key)
      } else {
        localStorage.setItem(rememberMarkerKey(key), 'session')
        sessionStorage.setItem(key, value)
        localStorage.removeItem(key)
      }
    } catch {
      try {
        sessionStorage.setItem(key, value)
      } catch {
        /* ignore */
      }
    }
  }
}
